/**
 * THE ONE DATE FORMAT (P38, task 7 — Roman's decision D5, audit S3-03)
 *
 * =========================================================================
 * WHY ISO, AND WHY IN ONE PLACE
 * =========================================================================
 * The audit found `date-fns` patterns written out by hand — `'yyyy-MM-dd'` nine
 * times, `'yyyy-MM-dd HH:mm'` eight times — plus one stray
 * `toLocaleDateString()` on `/settings` that produced a THIRD format on a page
 * that already had two, and a raw `2026-09-28T07:47:59Z` printed as-is beside
 * it. Three notations for one kind of fact, on one screen.
 *
 * Roman decided ISO everywhere (D5). It is the defensible choice for this app
 * and not merely the tidy one: the pilot is read in four languages, `28.09.2026`
 * and `09/28/2026` are the same string with opposite meanings, and every other
 * surface the DRA writes to — the file-name convention C10, `Dra.AuditTrail`,
 * the CSV exports — is already ISO. A reader comparing a board row against a
 * ledger row should not have to convert.
 *
 * The functions exist so the DECISION has a single site. A hand-written pattern
 * is not wrong today and drifts tomorrow: that is exactly how three formats got
 * onto one page.
 *
 * =========================================================================
 * WHAT THEY DO WITH A VALUE THEY CANNOT READ
 * =========================================================================
 * They return an em dash, they do not throw, and they do not invent. `format()`
 * on an `Invalid Date` throws a `RangeError`, which in a table cell takes the
 * whole page down — a null `dueAt` is the ordinary case (a due date is set on 4
 * of 54 tickets), and a malformed string from the database must not be able to
 * blank a board.
 *
 * `HH:mm` is LOCAL time, deliberately: this is the clock on the wall of the
 * office reading the screen, and it is what the old `format()` calls showed. The
 * one place a UTC instant is the fact itself — the agent's last poll on
 * `/settings` — says so in words beside the figure (`isoInstant`).
 */
import { format } from 'date-fns';

const DASH = '—';

function parse(value: string | number | Date | null | undefined): Date | null {
    if (value === null || value === undefined || value === '') return null;
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}

/** `2026-09-30`. A day, in local time. */
export function isoDate(value: string | number | Date | null | undefined): string {
    const d = parse(value);
    return d ? format(d, 'yyyy-MM-dd') : DASH;
}

/** `2026-09-30 14:07`. A moment, on the reader's own clock. */
export function isoDateTime(value: string | number | Date | null | undefined): string {
    const d = parse(value);
    return d ? format(d, 'yyyy-MM-dd HH:mm') : DASH;
}

/** `2026-09-30 14:07:59`, to the second — for a log line or a failure stamp. */
export function isoSeconds(value: string | number | Date | null | undefined): string {
    const d = parse(value);
    return d ? format(d, 'yyyy-MM-dd HH:mm:ss') : DASH;
}

/**
 * `2026-09-30 07:47 UTC`. The one case where the timezone is part of the fact:
 * the agent's checkpoints are written by a timer that runs in UTC, and a reader
 * comparing `lastPollCheckpoint` against a mail's own timestamp needs to be told
 * which clock it is. Before P38 this was the raw string with its `T` and its
 * `Z`, which is accurate and unreadable.
 */
export function isoInstant(value: string | number | Date | null | undefined): string {
    const d = parse(value);
    if (!d) return DASH;
    return `${d.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}
